/* Auren Sculpt Tank — page behaviors (loaded as a module: runs once even if included twice).
   1. Size finder (band + cup -> size) + size chart. Data: snippets/tank-config.liquid
   2. Buy box gallery thumbnails (legacy tank-buy section only)
   3. Bundle tiers (legacy TEMPORARY bundle, snippets/tank-bundle-temp.liquid; unused while
      the page runs on the native product section)
   4. Tank-only sticky add-to-cart bar (snippets/tank-sticky.liquid) */

const config = (() => {
  const el = document.querySelector('[data-tank-config]');
  try {
    return el ? JSON.parse(el.textContent) : null;
  } catch (e) {
    return null;
  }
})();

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* 1. Size finder ------------------------------------------------------------ */

// PROVISIONAL SIZE MAPPING — REPLACE WITH SUPPLIER DATA (values live in tank-config.liquid).
function recommend(band, cup) {
  const base = config.bandBase[band];
  const step = config.cupStep[cup];
  if (base === undefined || step === undefined) return null;
  const index = Math.min(base + step, config.sizes.length - 1);
  return config.sizes[index];
}

function chips(container, values) {
  const name = container.dataset.name;
  container.innerHTML = values
    .map(
      (v) =>
        `<label class="tk-chip"><input type="radio" name="${name}" value="${v}"><span>${v}</span></label>`
    )
    .join('');
}

function initFinder(root) {
  const bands = root.querySelector('[data-tank-bands]');
  const cups = root.querySelector('[data-tank-cups]');
  const result = root.querySelector('[data-tank-result]');
  const error = root.querySelector('[data-tank-error]');
  const chartToggle = root.querySelector('[data-tank-chart-toggle]');
  const chart = root.querySelector('[data-tank-chart]');
  const rows = root.querySelector('[data-tank-chart-rows]');
  let current = null;

  chips(bands, config.bands);
  chips(cups, config.cups);
  rows.innerHTML = config.chart
    .map((r) => `<tr data-size="${r.size}"><td>${r.size}</td><td>${r.bra}</td><td>${r.bust}</td></tr>`)
    .join('');

  // Standalone (always open) finder: keep it open.
  if (root.classList.contains('tk-finder--open')) {
    root.addEventListener('toggle', () => { if (!root.open) root.open = true; });
  }

  root.querySelector('[data-tank-find]').addEventListener('click', () => {
    const band = bands.querySelector('input:checked');
    const cup = cups.querySelector('input:checked');
    if (!band || !cup) {
      error.hidden = false;
      result.hidden = true;
      return;
    }
    error.hidden = true;
    current = recommend(band.value, cup.value);
    root.querySelector('[data-tank-size]').textContent = current;
    root.querySelector('[data-tank-note]').textContent =
      `Based on a ${band.value}${cup.value} bra. Choose ${current} for each tank below.`;
    result.hidden = false;
    rows.querySelectorAll('tr').forEach((tr) => tr.classList.toggle('is-match', tr.dataset.size === current));
  });

  const apply = root.querySelector('[data-tank-apply]');
  if (apply) {
    apply.addEventListener('click', () => {
      if (!current) return;
      document.dispatchEvent(new CustomEvent('tank:size', { detail: { size: current } }));
      apply.textContent = `Size ${current} applied to all tanks`;
    });
  }

  chartToggle.addEventListener('click', () => {
    const open = chart.hidden;
    chart.hidden = !open;
    chartToggle.setAttribute('aria-expanded', String(open));
    chartToggle.querySelector('span').textContent = open ? 'Hide size chart' : 'View full size chart';
  });
}

/* 2. Gallery ---------------------------------------------------------------- */

function initGallery(root) {
  const slides = root.querySelectorAll('[data-tank-slide]');
  const thumbs = root.querySelectorAll('[data-tank-thumb]');
  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const i = thumb.dataset.tankThumb;
      slides.forEach((s) => s.classList.toggle('is-active', s.dataset.tankSlide === i));
      thumbs.forEach((t) => {
        const on = t === thumb;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', String(on));
      });
    });
  });
}

/* 3. Bundle ----------------------------------------------------------------- */

function initBundle(form) {
  const template = form.querySelector('[data-tank-row]');
  const tiers = [...form.querySelectorAll('.tk-tier')];
  const error = form.querySelector('[data-tank-bundle-error]');
  const atc = form.querySelector('[data-tank-atc]');
  const atcPrice = form.querySelector('[data-tank-atc-price]');
  const stickyOffer = document.querySelector('[data-tank-sticky-offer]');
  // Selections survive tier switches: picks[i] = { color, size }.
  const picks = [];
  const defaultColor = config.colors[0].name;

  const colorOptions = config.colors.map((c) => `<option value="${c.name}">${c.name}</option>`).join('');
  const sizeOptions =
    '<option value="">Select size</option>' + config.sizes.map((s) => `<option value="${s}">${s}</option>`).join('');
  const hexOf = (name) => (config.colors.find((c) => c.name === name) || config.colors[0]).hex;

  function selectedTier() {
    return tiers.find((t) => t.querySelector('input').checked) || tiers[0];
  }

  function renderRows() {
    const tier = selectedTier();
    const input = tier.querySelector('input');
    const qty = parseInt(input.value, 10) || 1;

    tiers.forEach((t) => {
      const on = t === tier;
      t.classList.toggle('is-selected', on);
      const box = t.querySelector('[data-tank-items]');
      box.hidden = !on;
      if (!on) box.innerHTML = '';
    });

    const box = tier.querySelector('[data-tank-items]');
    box.innerHTML = '';
    for (let i = 0; i < qty; i++) {
      picks[i] = picks[i] || { color: defaultColor, size: '' };
      const row = template.content.firstElementChild.cloneNode(true);
      const color = row.querySelector('[data-tank-color]');
      const size = row.querySelector('[data-tank-size-select]');
      row.querySelector('.tk-row__num').textContent = qty > 1 ? `Tank ${i + 1}` : 'Your tank';
      color.innerHTML = colorOptions;
      size.innerHTML = sizeOptions;
      color.value = picks[i].color;
      size.value = picks[i].size;
      color.setAttribute('aria-label', `Tank ${i + 1} color`);
      size.setAttribute('aria-label', `Tank ${i + 1} size`);
      row.querySelector('.tk-row__swatch').style.setProperty('--swatch', hexOf(picks[i].color));
      color.addEventListener('change', () => {
        picks[i].color = color.value;
        row.querySelector('.tk-row__swatch').style.setProperty('--swatch', hexOf(color.value));
      });
      size.addEventListener('change', () => {
        picks[i].size = size.value;
        size.classList.remove('is-missing');
        if (form.querySelectorAll('select.is-missing').length === 0) error.hidden = true;
      });
      box.appendChild(row);
    }

    atcPrice.textContent = input.dataset.price || '';
    if (stickyOffer) stickyOffer.textContent = `${input.dataset.title} · ${input.dataset.price}`;
    error.hidden = true;
  }

  tiers.forEach((t) => t.querySelector('input').addEventListener('change', renderRows));

  document.addEventListener('tank:size', (e) => {
    picks.forEach((p) => { p.size = e.detail.size; });
    for (let i = picks.length; i < 6; i++) picks[i] = { color: defaultColor, size: e.detail.size };
    renderRows();
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const tier = selectedTier();
    const qty = parseInt(tier.querySelector('input').value, 10) || 1;
    const sizeSelects = [...tier.querySelectorAll('[data-tank-size-select]')];
    const missing = sizeSelects.filter((s) => !s.value);

    sizeSelects.forEach((s) => s.classList.toggle('is-missing', !s.value));
    if (missing.length) {
      error.textContent =
        missing.length === 1 ? 'Choose a size for your tank.' : `Choose a size for each of your ${qty} tanks.`;
      error.hidden = false;
      missing[0].focus();
      return;
    }

    const variantId = Number(form.dataset.variantId);
    if (!variantId) {
      error.textContent = 'This product is not available yet.';
      error.hidden = false;
      return;
    }

    const title = tier.querySelector('input').dataset.title;
    const items = picks.slice(0, qty).map((p) => ({
      id: variantId,
      quantity: 1,
      properties: { Color: p.color, Size: p.size, _Offer: title }
    }));

    atc.disabled = true;
    try {
      const res = await fetch(`${form.dataset.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description || data.message || 'Could not add to cart.');
      }
      window.location.href = form.dataset.cartUrl;
    } catch (err) {
      error.textContent = err.message || 'Could not add to cart. Please try again.';
      error.hidden = false;
      atc.disabled = false;
    }
  });

  renderRows();
}

/* 4. Sticky add to cart ------------------------------------------------------ */

function initSticky(bar) {
  const hero = document.querySelector('.tk-hero');
  // "Buy area in view" = the native product section holding the Tank intro block
  // (falls back to the legacy tank-buy section).
  const buy =
    document.querySelector('[data-tank-product]')?.closest('.shopify-section') ||
    document.querySelector('section#tank-buy');
  // The final CTA already carries its own button: hide the bar there to avoid two CTAs.
  const final = document.querySelector('.tk-final');
  const link = bar.querySelector('a');
  if (!('IntersectionObserver' in window) || !buy) return;
  const state = { hero: true, buy: false, final: false };

  const update = () => {
    const show = !state.hero && !state.buy && !state.final;
    bar.classList.toggle('is-visible', show);
    bar.setAttribute('aria-hidden', String(!show));
    link.tabIndex = show ? 0 : -1;
  };

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.target === hero) state.hero = entry.isIntersecting;
      if (entry.target === buy) state.buy = entry.isIntersecting;
      // Hidden from the final CTA down, so the bar never covers the footer either.
      if (entry.target === final) state.final = entry.isIntersecting || entry.boundingClientRect.top < 0;
    });
    update();
  });
  if (hero) io.observe(hero);
  else state.hero = false;
  io.observe(buy);
  if (final) io.observe(final);

  link.addEventListener('click', (e) => {
    e.preventDefault();
    // Works with the temporary bundle or, later, the MoonBundle block in the purchase area.
    const target =
      document.querySelector('.tk-bundle__tiers') ||
      buy.querySelector('product-form-component, form[action*="/cart/add"]') ||
      buy;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  });
}

/* Init ---------------------------------------------------------------------- */

if (config) {
  document.querySelectorAll('[data-tank-finder]').forEach(initFinder);
  document.querySelectorAll('[data-tank-bundle]').forEach(initBundle);
}
document.querySelectorAll('[data-tank-gallery]').forEach(initGallery);
document.querySelectorAll('[data-tank-sticky]').forEach(initSticky);
